/**
 * Cashflow report table helpers — pure UI helpers, no API/business logic.
 */

export const TYPE_KEYS = ['uang_masuk', 'uang_keluar']

export function getReportData (list) {
  if (!list || list.status !== 200) return null
  return (list.results && list.results.data) || null
}

export function getRekapitulasi (list) {
  const data = getReportData(list)
  return (data && data.rekapitulasi) || null
}

export function formatAmount (value) {
  if (value === null || value === undefined || value === '') return '0'
  const num = Number(value)
  if (!isFinite(num)) return '0'

  const negative = num < 0
  const abs = Math.abs(Math.round(num))
  const reverse = String(abs).split('').reverse().join('')
  let formatted = ''

  for (let i = 0; i < reverse.length; i++) {
    if (i > 0 && i % 3 === 0) formatted += '.'
    formatted += reverse[i]
  }

  const result = formatted.split('').reverse().join('')
  return negative ? `-${result}` : result
}

export function headerLabel (header) {
  if (!header) return ''
  return header.header_label ||
    [header.cashflow_sumber_coa_number, header.cashflow_sumber_coa_name]
      .filter(Boolean)
      .join(' - ')
}

export function headerId (header) {
  return header && header.cashflow_sumber_coa_id
}

/**
 * Resolve nominal for a sumber column from nominal_by_sumber_array.
 * Matches by cashflow_sumber_coa_id — works for any column count.
 */
export function getSumberNominal (nominalBySumberArray, sumberCoaId) {
  if (!Array.isArray(nominalBySumberArray) || sumberCoaId == null) {
    return { total_nominal: 0, total_transaksi: 0 }
  }

  const found = nominalBySumberArray.find(
    item => String(item.cashflow_sumber_coa_id) === String(sumberCoaId)
  )

  return {
    total_nominal: found ? Number(found.total_nominal) || 0 : 0,
    total_transaksi: found ? Number(found.total_transaksi) || 0 : 0
  }
}

/** Sum detail rows into per-sumber totals (for type/category subtotal columns). */
export function sumNominalBySumber (rowArray = []) {
  const map = {}

  ;(rowArray || []).forEach(row => {
    ;(row.nominal_by_sumber_array || []).forEach(item => {
      const id = item.cashflow_sumber_coa_id
      if (id == null) return

      if (!map[id]) {
        map[id] = {
          cashflow_sumber_coa_id: id,
          total_nominal: 0,
          total_transaksi: 0
        }
      }

      map[id].total_nominal += Number(item.total_nominal) || 0
      map[id].total_transaksi += Number(item.total_transaksi) || 0
    })
  })

  return Object.keys(map).map(key => map[key])
}

function mergeSumberValues (a = [], b = []) {
  const map = {}

  ;[...(a || []), ...(b || [])].forEach(item => {
    const id = item.cashflow_sumber_coa_id
    if (id == null) return

    if (!map[id]) {
      map[id] = {
        cashflow_sumber_coa_id: id,
        total_nominal: 0,
        total_transaksi: 0
      }
    }

    map[id].total_nominal += Number(item.total_nominal) || 0
    map[id].total_transaksi += Number(item.total_transaksi) || 0
  })

  return Object.keys(map).map(key => map[key])
}

/** Classify sumber as kas / bank from API name fields (no hardcoded IDs). */
export function isKasSumber (item) {
  const name = String(
    (item && (item.cashflow_sumber_coa_name || item.header_label)) || ''
  ).toUpperCase()

  if (name.includes('BANK')) return false
  if (name.includes('KAS')) return true
  return false
}

export function isBankSumber (item) {
  const name = String(
    (item && (item.cashflow_sumber_coa_name || item.header_label)) || ''
  ).toUpperCase()

  return name.includes('BANK')
}

function sumArrayBy (arr, predicate) {
  return (arr || []).reduce(
    (acc, item) => {
      if (!predicate(item)) return acc
      acc.total_nominal += Number(item.total_nominal) || 0
      acc.total_transaksi += Number(item.total_transaksi) || 0
      return acc
    },
    { total_nominal: 0, total_transaksi: 0 }
  )
}

/**
 * Flatten group_array into render rows:
 * Category → Type → Detail → Subtotal → Category Total
 */
export function buildTableRows (groupArray = []) {
  const rows = []

  ;(groupArray || []).forEach((group, gIndex) => {
    rows.push({
      id: `cat-${group.category_key || gIndex}`,
      kind: 'category',
      label: group.category_label || group.category_key || '',
      category_key: group.category_key
    })

    const categoryValuesParts = []

    TYPE_KEYS.forEach(typeKey => {
      const typeBlock = group[typeKey]
      if (!typeBlock) return

      const typeLabel = typeBlock.label || typeKey
      const typeValues = sumNominalBySumber(typeBlock.row_array)
      categoryValuesParts.push(typeValues)

      rows.push({
        id: `type-${group.category_key || gIndex}-${typeKey}`,
        kind: 'type',
        label: typeLabel,
        type_key: typeKey,
        category_key: group.category_key
      })

      ;(typeBlock.row_array || []).forEach((detail, dIndex) => {
        rows.push({
          id: `detail-${detail.cashflow_id || `${gIndex}-${typeKey}-${dIndex}`}`,
          kind: 'detail',
          coa_number: detail.cashflow_coa_number || '',
          coa_name: detail.cashflow_coa_name || '',
          nominal_by_sumber_array: detail.nominal_by_sumber_array || [],
          grand_total: Number(detail.row_total_nominal) || 0,
          total_transaksi: Number(detail.row_total_transaksi) || 0,
          type_key: typeKey,
          category_key: group.category_key
        })
      })

      rows.push({
        id: `subtotal-${group.category_key || gIndex}-${typeKey}`,
        kind: 'subtotal',
        label: `Subtotal ${typeLabel}`,
        values: typeValues,
        grand_total: Number(typeBlock.total_nominal) || 0,
        total_transaksi: Number(typeBlock.total_transaksi) || 0,
        type_key: typeKey,
        category_key: group.category_key
      })
    })

    rows.push({
      id: `cat-total-${group.category_key || gIndex}`,
      kind: 'category_total',
      label: `Subtotal ${group.category_label || group.category_key || ''}`,
      values: categoryValuesParts.reduce((acc, part) => mergeSumberValues(acc, part), []),
      grand_total: Number(group.total_nominal) || 0,
      total_transaksi: Number(group.total_transaksi) || 0,
      category_key: group.category_key
    })
  })

  return rows
}

/**
 * Footer rows matching Excel summary by sumber dana + detail sections.
 */
export function buildFooterRows (rekapitulasi) {
  if (!rekapitulasi) return []

  const byType = rekapitulasi.column_total_by_type_array || []
  const masukArr = rekapitulasi.subtotal_uang_masuk_per_sumber_dana_array || []
  const keluarArr = rekapitulasi.subtotal_uang_keluar_per_sumber_dana_array || []
  const masukTotal = rekapitulasi.subtotal_uang_masuk_per_sumber_dana || {}
  const keluarTotal = rekapitulasi.subtotal_uang_keluar_per_sumber_dana || {}
  const totals = rekapitulasi.column_total_array || []
  const perTypeDetail = rekapitulasi.subtotal_per_sumber_dana_per_type_array || []
  const rows = []

  if (masukArr.length || byType.length) {
    const values = masukArr.length
      ? masukArr.map(item => ({
          cashflow_sumber_coa_id: item.cashflow_sumber_coa_id,
          total_nominal: Number(item.total_nominal) || 0,
          total_transaksi: Number(item.total_transaksi) || 0
        }))
      : byType.map(item => ({
          cashflow_sumber_coa_id: item.cashflow_sumber_coa_id,
          total_nominal: Number(item.uang_masuk_nominal) || 0,
          total_transaksi: Number(item.uang_masuk_transaksi) || 0
        }))

    rows.push({
      id: 'footer-masuk',
      kind: 'footer',
      label: 'SUBTOTAL UANG MASUK PER SUMBER DANA',
      values,
      grand_total: masukArr.length
        ? Number(masukTotal.total_nominal) || values.reduce((s, i) => s + i.total_nominal, 0)
        : byType.reduce((s, i) => s + (Number(i.uang_masuk_nominal) || 0), 0),
      total_transaksi: masukArr.length
        ? Number(masukTotal.total_transaksi) || values.reduce((s, i) => s + i.total_transaksi, 0)
        : byType.reduce((s, i) => s + (Number(i.uang_masuk_transaksi) || 0), 0)
    })
  }

  if (keluarArr.length || byType.length) {
    const values = keluarArr.length
      ? keluarArr.map(item => ({
          cashflow_sumber_coa_id: item.cashflow_sumber_coa_id,
          total_nominal: Number(item.total_nominal) || 0,
          total_transaksi: Number(item.total_transaksi) || 0
        }))
      : byType.map(item => ({
          cashflow_sumber_coa_id: item.cashflow_sumber_coa_id,
          total_nominal: Number(item.uang_keluar_nominal) || 0,
          total_transaksi: Number(item.uang_keluar_transaksi) || 0
        }))

    rows.push({
      id: 'footer-keluar',
      kind: 'footer',
      label: 'SUBTOTAL UANG KELUAR PER SUMBER DANA',
      values,
      grand_total: keluarArr.length
        ? Number(keluarTotal.total_nominal) || values.reduce((s, i) => s + i.total_nominal, 0)
        : byType.reduce((s, i) => s + (Number(i.uang_keluar_nominal) || 0), 0),
      total_transaksi: keluarArr.length
        ? Number(keluarTotal.total_transaksi) || values.reduce((s, i) => s + i.total_transaksi, 0)
        : byType.reduce((s, i) => s + (Number(i.uang_keluar_transaksi) || 0), 0)
    })
  }

  if (totals.length) {
    rows.push({
      id: 'footer-total',
      kind: 'footer_total',
      label: 'TOTAL PER SUMBER DANA',
      values: totals.map(item => ({
        cashflow_sumber_coa_id: item.cashflow_sumber_coa_id,
        total_nominal: Number(item.total_nominal) || 0,
        total_transaksi: Number(item.total_transaksi) || 0
      })),
      grand_total: totals.reduce((s, i) => s + (Number(i.total_nominal) || 0), 0),
      total_transaksi: totals.reduce((s, i) => s + (Number(i.total_transaksi) || 0), 0)
    })
  }

  // Excel: DETAIL SUBTOTAL PER SUMBER DANA PER TYPE
  // if (perTypeDetail.length) {
  //   rows.push({
  //     id: 'section-detail-subtotal',
  //     kind: 'category',
  //     label: 'DETAIL SUBTOTAL PER SUMBER DANA PER TYPE'
  //   })

  //   perTypeDetail.forEach((item, index) => {
  //     const label = item.header_label ||
  //       [item.cashflow_sumber_coa_number, item.cashflow_sumber_coa_name]
  //         .filter(Boolean)
  //         .join(' - ')

  //     const masuk = item.uang_masuk || {}
  //     const keluar = item.uang_keluar || {}

  //     rows.push({
  //       id: `detail-subtotal-masuk-${item.cashflow_sumber_coa_id || index}`,
  //       kind: 'subtotal',
  //       label: `Sub Total Sumber Dana "${label}" di Uang Masuk`,
  //       wrap_label: true,
  //       grand_total: Number(masuk.total_nominal) || 0,
  //       total_transaksi: Number(masuk.total_transaksi) || 0
  //     })

  //     rows.push({
  //       id: `detail-subtotal-keluar-${item.cashflow_sumber_coa_id || index}`,
  //       kind: 'subtotal',
  //       label: `Sub Total Sumber Dana "${label}" di Uang Keluar`,
  //       wrap_label: true,
  //       grand_total: Number(keluar.total_nominal) || 0,
  //       total_transaksi: Number(keluar.total_transaksi) || 0
  //     })
  //   })
  // }

  return rows
}

/**
 * Kas/Bank summary — rendered outside the table (Excel bottom section).
 */
export function buildSumberDanaSummary (rekapitulasi) {
  if (!rekapitulasi) return null

  const masukArr = rekapitulasi.subtotal_uang_masuk_per_sumber_dana_array || []
  const keluarArr = rekapitulasi.subtotal_uang_keluar_per_sumber_dana_array || []
  const masukTotal = rekapitulasi.subtotal_uang_masuk_per_sumber_dana || {}
  const keluarTotal = rekapitulasi.subtotal_uang_keluar_per_sumber_dana || {}

  if (!masukArr.length && !keluarArr.length) return null

  const masukKas = sumArrayBy(masukArr, isKasSumber)
  const masukBank = sumArrayBy(masukArr, isBankSumber)
  const keluarKas = sumArrayBy(keluarArr, isKasSumber)
  const keluarBank = sumArrayBy(keluarArr, isBankSumber)

  return {
    uang_masuk: {
      kas: masukKas,
      bank: masukBank,
      total: {
        total_nominal: Number(masukTotal.total_nominal) || (masukKas.total_nominal + masukBank.total_nominal),
        total_transaksi: Number(masukTotal.total_transaksi) || (masukKas.total_transaksi + masukBank.total_transaksi)
      }
    },
    uang_keluar: {
      kas: keluarKas,
      bank: keluarBank,
      total: {
        total_nominal: Number(keluarTotal.total_nominal) || (keluarKas.total_nominal + keluarBank.total_nominal),
        total_transaksi: Number(keluarTotal.total_transaksi) || (keluarKas.total_transaksi + keluarBank.total_transaksi)
      }
    }
  }
}

export function footerValueForHeader (footerRow, sumberCoaId) {
  if (!footerRow || !Array.isArray(footerRow.values)) {
    return { total_nominal: 0, total_transaksi: 0 }
  }

  const found = footerRow.values.find(
    item => String(item.cashflow_sumber_coa_id) === String(sumberCoaId)
  )

  return {
    total_nominal: found ? Number(found.total_nominal) || 0 : 0,
    total_transaksi: found ? Number(found.total_transaksi) || 0 : 0
  }
}
